import { useMemo } from 'react';
import type { EnrolledCourse } from '@lp/shared';
import { Link } from 'react-router';
import { BookOpenText, Mail, Sparkles } from 'lucide-react';
import { PageContainer, PageHeading } from '../components/layout/AppShell.js';
import { Alert, EmptyState, SectionHeading } from '../components/ui.js';
import { CourseCard } from '../components/learner/CourseCard.js';
import { ApiErrorView, CourseCardsSkeleton } from '../components/learner/StateViews.js';
import { useCourses } from '../lib/useLearnerData.js';
import { useMe } from '../lib/auth.js';

/**
 * My learning.
 *
 * The important state here is the empty one. A new joiner is provisioned the
 * first time they sign in, which means an empty dashboard is the *correct*
 * answer for them, not a failure — so it reads as a welcome with a next step,
 * never as an error.
 */
export function Dashboard() {
  const me = useMe();
  const { data, isPending, error, refetch } = useCourses();

  const { live, drafts } = useMemo(() => {
    const all = data ?? [];
    return {
      live: all.filter((course: EnrolledCourse) => course.status === 'published'),
      drafts: all.filter((course: EnrolledCourse) => course.status !== 'published'),
    };
  }, [data]);

  const firstName = me.user.name.trim().split(/\s+/)[0] ?? me.user.name;

  /* Every figure below is counted from the courses already on this page —
     nothing is estimated and nothing is fetched twice. */
  const glance = useMemo(() => {
    const done = live.reduce((n, c) => n + c.progress.completedSessions, 0);
    const total = live.reduce((n, c) => n + c.progress.totalSessions, 0);
    return {
      courses: live.length,
      sessionsDone: done,
      sessionsTotal: total,
      capstones: live.filter((c) => c.hasCapstone).length,
    };
  }, [live]);

  return (
    <PageContainer>
      <PageHeading
        title={`Hello, ${firstName}`}
        description={
          isPending
            ? 'Fetching your courses…'
            : live.length > 0
              ? 'Pick up where you left off, or start something new.'
              : 'Everything assigned to you shows up here.'
        }
      />

      {isPending ? <CourseCardsSkeleton /> : null}

      {error ? (
        <ApiErrorView error={error} retry={() => void refetch()} context="your courses" />
      ) : null}

      {!isPending && !error ? (
        <>
          {live.length > 0 ? <AtAGlance {...glance} /> : null}

          {live.length > 0 ? (
            <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-label="Your courses">
              {live.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))}
            </ul>
          ) : (
            <NothingEnrolled />
          )}

          {drafts.length > 0 ? (
            <section className="mt-10" aria-labelledby="draft-courses">
              <SectionHeading id="draft-courses">Your drafts</SectionHeading>
              {/* No wrapper card: the alert already says what these are, and a
                  card holding cards reads as a rendering accident. */}
              <div>
                <Alert tone="warn" title="Only you can see these" className="mb-4">
                  These courses are unpublished. They are on your dashboard because you author
                  something inside them — no learner has them yet, and publishing is what changes
                  that.
                </Alert>
                <ul
                  className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
                  aria-label="Courses you are working on"
                >
                  {drafts.map((course) => (
                    <CourseCard key={course.id} course={course} />
                  ))}
                </ul>
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </PageContainer>
  );
}

/**
 * A three-figure strip above the cards.
 *
 * It exists for the reader who opens this page to answer "where am I", not to
 * click into a course — the manager as much as the learner. Every number is
 * counted from the same course list rendered below it, so the strip can never
 * disagree with the cards.
 */
function AtAGlance({
  courses,
  sessionsDone,
  sessionsTotal,
  capstones,
}: {
  courses: number;
  sessionsDone: number;
  sessionsTotal: number;
  capstones: number;
}) {
  const pct = sessionsTotal > 0 ? Math.round((sessionsDone / sessionsTotal) * 100) : 0;
  const figures = [
    { label: 'Enrolled in', value: String(courses), note: 'published and open to you' },
    {
      label: 'Sessions done',
      value: `${sessionsDone}/${sessionsTotal}`,
      note: sessionsTotal > 0 ? `${pct}% of the material` : 'nothing published yet',
    },
    {
      label: 'Capstones',
      value: String(capstones),
      note: capstones === 0 ? 'none on your courses' : 'ungraded, yours to build',
    },
  ];

  return (
    <section
      aria-label="Where you are"
      className="mb-6 rounded-card border border-line bg-surface divide-y divide-line sm:divide-y-0 sm:divide-x sm:grid sm:grid-cols-3"
    >
      {figures.map((f) => (
        <div key={f.label} className="px-5 py-4">
          <p className="eyebrow">{f.label}</p>
          <p className="font-mono text-2xl font-semibold tabular-nums text-ink mt-1.5 leading-none">
            {f.value}
          </p>
          <p className="text-xs text-ink-muted mt-1.5">{f.note}</p>
        </div>
      ))}
    </section>
  );
}

function NothingEnrolled() {
  return (
    <EmptyState
      icon={<Sparkles size={28} aria-hidden />}
      title="You are not enrolled in anything yet"
      description={
        <>
          Your account is set up and working — there is simply nothing assigned to it so far.
          Courses are added by the team that runs the programme, and as soon as one is you will
          find it here with no further setup on your side.
        </>
      }
      action={
        <div className="flex flex-col items-center gap-3">
          <p className="inline-flex items-center gap-2 text-sm text-ink-muted">
            <Mail size={15} aria-hidden />
            Expecting a course? Ask the person who invited you to enroll you.
          </p>
          <Link
            to="/guides"
            className="inline-flex items-center gap-2 rounded-control border border-control-line bg-surface px-3.5 py-2 text-sm font-medium text-ink transition-colors duration-150 ease-out hover:bg-surface-2"
          >
            <BookOpenText size={15} aria-hidden />
            In the meantime, the guides are open to everyone
          </Link>
        </div>
      }
    />
  );
}
